import { useEffect, useState } from "react";
import { Activity, Globe2, Zap, Timer, RefreshCw } from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { codeToFlag, formatDuration, formatRelative } from "@/lib/format";

function StatCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div
      className="speculo-glass rounded-sm p-5 relative overflow-hidden transition-all duration-200 hover:speculo-glow"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-cyan-500/80 font-semibold">
          {label}
        </span>
        <Icon className="w-4 h-4 text-cyan-400/70" />
      </div>
      <div
        className="mt-4 text-4xl font-mono text-cyan-300 font-semibold tabular-nums"
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      {hint && (
        <div className="mt-2 text-[11px] text-slate-400 uppercase tracking-widest">{hint}</div>
      )}
      <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-cyan-400/5 blur-2xl pointer-events-none" />
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const [sumRes, logRes] = await Promise.all([
        api.get("/telemetry/stats/summary"),
        api.get("/telemetry/logs", { params: { limit: 25 } }),
      ]);
      setSummary(sumRes.data);
      setLogs(logRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load dashboard");
    }
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-cyan-100 tracking-[0.12em] uppercase">
            Command Deck
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-lg">
            Live telemetry across your workspace. Data streams refresh every 30 seconds; a{" "}
            <span className="text-cyan-300">globe view</span> is inbound in Phase 2.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-cyan-500/30 text-cyan-300 uppercase tracking-widest text-xs hover:bg-cyan-950/40 transition-all disabled:opacity-50"
          data-testid="dashboard-refresh-button"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Syncing" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          className="mb-6 text-sm text-[#ff4d70] border border-[#FF003C]/40 bg-[#FF003C]/10 rounded-sm px-4 py-3 font-mono"
          data-testid="dashboard-error"
        >
          {error}
        </div>
      )}

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        data-testid="summary-stats"
      >
        <StatCard
          label="Active Users · 30m"
          value={loading ? "—" : summary?.total_active_users ?? 0}
          hint="Distinct users pinged in last 30 minutes"
          icon={Activity}
          testid="stat-active-users"
        />
        <StatCard
          label="Countries"
          value={loading ? "—" : summary?.total_countries ?? 0}
          hint="Distinct resolved territories"
          icon={Globe2}
          testid="stat-countries"
        />
        <StatCard
          label="Pings Today"
          value={loading ? "—" : summary?.total_pings_today ?? 0}
          hint="Aggregated across all apps"
          icon={Zap}
          testid="stat-pings-today"
        />
        <StatCard
          label="Avg Session · Today"
          value={loading ? "—" : formatDuration(summary?.avg_session_duration_seconds ?? 0)}
          hint="Mean total_duration per record"
          icon={Timer}
          testid="stat-avg-session"
        />
      </section>

      <section className="speculo-glass rounded-sm overflow-hidden" data-testid="logs-section">
        <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-500/15">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full bg-cyan-400 speculo-pulse" />
            <h2 className="text-sm uppercase tracking-[0.3em] text-cyan-300 font-semibold">
              Recent Telemetry
            </h2>
          </div>
          <span className="text-[11px] text-slate-500 uppercase tracking-widest font-mono">
            {logs.length} records · newest first
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.25em] text-cyan-500/70 border-b border-cyan-500/10">
                <th className="text-left px-5 py-3 font-semibold">Country</th>
                <th className="text-left px-5 py-3 font-semibold">App</th>
                <th className="text-left px-5 py-3 font-semibold">User</th>
                <th className="text-left px-5 py-3 font-semibold">City</th>
                <th className="text-right px-5 py-3 font-semibold">Duration</th>
                <th className="text-right px-5 py-3 font-semibold">Pings</th>
                <th className="text-right px-5 py-3 font-semibold">Last Ping</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-6 text-center text-slate-500 font-mono text-xs">
                    Streaming records…
                  </td>
                </tr>
              )}
              {!loading && logs.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-5 py-8 text-center text-slate-500 text-xs uppercase tracking-widest"
                    data-testid="logs-empty"
                  >
                    No telemetry yet. Send a ping to see it flow in.
                  </td>
                </tr>
              )}
              {logs.map((log, idx) => (
                <tr
                  key={log.id}
                  className="border-b border-cyan-500/5 hover:bg-cyan-950/30 transition-colors speculo-row-anim"
                  style={{ animationDelay: `${Math.min(idx, 20) * 20}ms` }}
                  data-testid={`log-row-${log.id}`}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg leading-none">{codeToFlag(log.country_code)}</span>
                      <div>
                        <div className="text-slate-100 text-sm">{log.country_name || "—"}</div>
                        <div className="text-[10px] text-cyan-500/70 uppercase font-mono">
                          {log.country_code}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-200">{log.app_name || log.app_id}</td>
                  <td className="px-5 py-3 font-mono text-cyan-300 text-xs">{log.user_id}</td>
                  <td className="px-5 py-3 text-slate-400 text-sm">{log.city || "—"}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-200">
                    {formatDuration(log.session_duration)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-cyan-300">{log.ping_count}</td>
                  <td className="px-5 py-3 text-right font-mono text-slate-500 text-xs">
                    {formatRelative(log.last_ping_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
